import React, { useState } from "react";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import useApiUrls from "../../useApiUrls";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";

const AddFirmware = () => {
  const navigate = useNavigate();
  const { id, name } = useParams();
  const { getUrlByOperation } = useApiUrls();

  // State to hold form data
  const [formData, setFormData] = useState({
    FirmwareName: "",
    FirmwareVersion: "",
    Remark: "",
    Active: true,
    File: null
  });

  // State to hold API response or error
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent the form from refreshing the page

    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      console.error("Missing token or userId in localStorage");
      return;
    }

    // Validate file size (max 50MB)
    if (formData.File && formData.File.size > 50 * 1024 * 1024) {
      setMessage("File size must be less than 50MB");
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    setMessage("");

    // Create FormData object for multipart/form-data
    const formDataToSend = new FormData();
    formDataToSend.append("FirmwareName", formData.FirmwareName);
    formDataToSend.append("FirmwareVersion", formData.FirmwareVersion);
    formDataToSend.append("Remark", formData.Remark);
    formDataToSend.append("Active", formData.Active);
    if (formData.File) {
      formDataToSend.append("File", formData.File);
    }

    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/FirmwareOperation/Insert',
        formDataToSend,
        {
          headers: {
            "accept": "text/plain",
            "X-Authorization": token,
            "UserId": userid,
            "Content-Type": "multipart/form-data",
          },
          timeout: 300000, // 5 minutes timeout for file upload
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          },
          maxContentLength: 50 * 1024 * 1024, // 50MB max file size
          maxBodyLength: 50 * 1024 * 1024, // 50MB max body size
        }
      );
      
      setMessage("Firmware added successfully!");
      setUploadProgress(100);

      // Navigate to another page after 2 seconds
      setTimeout(() => {
        navigate("/Firmware");
      }, 2000);
    } catch (error) {
      console.error("Error adding firmware:", error);
      if (error.code === 'ECONNABORTED') {
        setMessage("Upload timeout. Please try again with a smaller file or check your internet connection.");
      } else if (error.response?.status === 413) {
        setMessage("File too large. Please choose a smaller file (max 50MB).");
      } else if (error.response?.status === 401) {
        setMessage("Authentication failed. Please login again.");
      } else if (error.message?.includes('maxContentLength')) {
        setMessage("File size exceeds the maximum allowed limit (50MB).");
      } else {
        setMessage(`Failed to add Firmware: ${error.response?.data?.errormsg || error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle input change for text inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prevData) => ({
      ...prevData,
      File: file
    }));
    
    // Clear previous messages when file changes
    if (file) {
      const fileSize = (file.size / 1024 / 1024).toFixed(2);
      console.log(`Selected file: ${file.name}, Size: ${fileSize}MB`);
    }
    setMessage("");
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6"></div>
              </div>
            </div>
          </section>
          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">Add Firmware</h3>
                    </div>
                    <div className="card-body">
                      <form id="add-items" onSubmit={handleSubmit}>
                        <div className="row">
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label>Firmware Name</label>
                              <input
                                type="text"
                                className="form-control"
                                value={formData.FirmwareName}
                                onChange={handleChange}
                                placeholder="Enter firmware name"
                                name="FirmwareName"
                                required
                              />
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label>Firmware Version</label>
                              <input
                                type="text"
                                className="form-control"
                                value={formData.FirmwareVersion}
                                onChange={handleChange}
                                placeholder="Enter version"
                                name="FirmwareVersion"
                                required
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label>Remark</label>
                              <input
                                type="text"
                                className="form-control"
                                value={formData.Remark}
                                onChange={handleChange}
                                placeholder="Enter remark"
                                name="Remark"
                              />
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label>Active</label>
                              <select
                                className="form-control"
                                name="Active"
                                value={formData.Active}
                                onChange={handleChange}
                              >
                                <option value={true}>Active</option>
                                <option value={false}>Deactive</option>
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label>Firmware File</label>
                              <input
                                type="file"
                                className="form-control"
                                onChange={handleFileChange}
                                accept=".zip,application/zip,application/x-zip-compressed,.apk,application/vnd.android.package-archive"
                                disabled={loading}
                                required
                              />
                              {formData.File && (
                                <small className="text-info">
                                  Selected: {formData.File.name} ({(formData.File.size / 1024 / 1024).toFixed(2)}MB)
                                </small>
                              )}
                              <small className="text-muted d-block">Upload a zip or .apk file (max 50MB)</small>
                            </div>
                          </div>
                        </div>

                        {loading && (
                          <div className="row">
                            <div className="col-sm-6">
                              <div className="form-group">
                                <label>Upload Progress</label>
                                <div className="progress">
                                  <div 
                                    className="progress-bar progress-bar-striped progress-bar-animated" 
                                    role="progressbar"
                                    style={{ width: `${uploadProgress}%` }}
                                    aria-valuenow={uploadProgress} 
                                    aria-valuemin="0" 
                                    aria-valuemax="100"
                                  >
                                    {uploadProgress}%
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="form-footer">
                          <button
                            type="submit"
                            id="add-sub"
                            className="btn btn-info"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                {' '}Uploading...
                              </>
                            ) : (
                              'Add'
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {message === "Firmware added successfully!" && (
        <SuccessMessage message={message} />
      )}

      {message === "Failed to add Firmware." && (
        <FailedMessage message={message} />
      )}
    </>
  );
};

export default AddFirmware;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}
